import type { TransferStats } from '../transfer/types';

interface Sample {
  t: number;
  bytes: number;
}

/**
 * Tracks transfer progress and derives instantaneous speed + ETA over a sliding
 * time window (so the numbers stay responsive without jittering every frame).
 */
export class ProgressTracker {
  private bytes = 0;
  private chunksDone = 0;
  private readonly samples: Sample[] = [];
  private readonly windowMs = 3000;

  constructor(
    private readonly totalBytes: number,
    private readonly totalChunks: number,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.samples.push({ t: this.now(), bytes: 0 });
  }

  /** Record that `bytes` more bytes (and one chunk) completed. */
  addChunk(bytes: number): void {
    this.bytes += bytes;
    this.chunksDone += 1;
    this.record();
  }

  /** Set absolute progress (used when resuming from a checkpoint). */
  setProgress(bytes: number, chunksDone: number): void {
    this.bytes = bytes;
    this.chunksDone = chunksDone;
    this.record();
  }

  private record(): void {
    const t = this.now();
    this.samples.push({ t, bytes: this.bytes });
    const cutoff = t - this.windowMs;
    while (this.samples.length > 2 && this.samples[0].t < cutoff) {
      this.samples.shift();
    }
  }

  snapshot(): TransferStats {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const dt = (last.t - first.t) / 1000;
    const db = last.bytes - first.bytes;
    const speed = dt > 0 ? db / dt : 0;
    const remaining = this.totalBytes - this.bytes;
    const eta = speed > 0 && remaining > 0 ? remaining / speed : remaining <= 0 ? 0 : null;
    return {
      bytesTransferred: this.bytes,
      totalBytes: this.totalBytes,
      percent: this.totalBytes > 0 ? (this.bytes / this.totalBytes) * 100 : 100,
      speedBytesPerSec: speed,
      etaSeconds: eta,
      chunksDone: this.chunksDone,
      totalChunks: this.totalChunks,
    };
  }
}
